import { Controller } from '@hotwired/stimulus';

/**
 * Visa Tracker Controller — compagnon du contrôleur `toggle` sur `Ui::VisaTrackerComponent` (R-25).
 *
 * Monté SEULEMENT quand le composant reçoit `toggle_labels:` ou `storage_key:` : l'appel historique
 * (OS, DGD, TS) garde `data-controller="toggle"` seul. `toggle` ouvre et ferme ; ce contrôleur :
 *   - remplace le libellé du basculement (« Voir le suivi » ↔ « Masquer le suivi ») par `textContent` ;
 *   - avec `toggle_aria_labels:` (tour H, A01-13), bascule AUSSI l'`aria-label` du bouton (« Voir le suivi de
 *     la validation » ↔ « Masquer le suivi de la validation »), sinon le nom accessible mentirait sur l'état ;
 *   - mémorise l'état ouvert dans `sessionStorage[storageKey]` et le RÉAPPLIQUE à la connexion : le
 *     partiel est remplacé par Turbo après une écriture et revient replié du serveur, qui ignore le
 *     choix du navigateur. Accès au stockage protégé (navigation privée, iframe cloisonnée) ;
 *   - le RÉAPPLIQUE aussi après un MORPH (`_validation_refresh`, `method: :morph`) : idiomorph garde
 *     l'élément, `connect()` ne rejoue pas, et le rendu serveur replié passe `data-toggle-open-value` à
 *     « false ». Turbo émet `turbo:morph-element` sur l'hôte APRÈS avoir morphé ses attributs et ses
 *     enfants, de façon synchrone, donc avant la microtâche du MutationObserver de Stimulus : `morphed`
 *     suspend l'écriture (le `toggle:closed` qui suit vient du serveur, pas de l'utilisateur), puis remet
 *     l'état mémorisé dans une microtâche mise en file APRÈS celle de Stimulus — `toggle` voit alors un vrai
 *     changement et redessine panneau, `aria-expanded` et chevron.
 *
 * Targets:
 * - label: le texte du basculement
 * - trigger: le bouton de basculement (seulement avec `toggle_aria_labels:`), dont l'`aria-label` bascule
 *
 * Values:
 * - showLabel (String): libellé replié
 * - hideLabel (String): libellé déplié
 * - showAriaLabel / hideAriaLabel (String): `aria-label` du bouton replié / déplié (vide = non écrit)
 * - storageKey (String): clé sessionStorage, vide = rien de mémorisé
 * - anchor (String): id de l'ancre qui OUVRE le suivi quand le hash de la page la vise (QA-PR1-19, A04
 *   légende 2 : la ligne « Validation » d'« À traiter » pointe `#` + `dom_id(plan, :validation_anchor)`).
 *   Ouvert à la connexion, sur `hashchange` et sur le clic d'un lien vers elle (hash déjà le bon, aucun
 *   `hashchange`) ; l'état ouvert est mémorisé, donc réappliqué après un morph comme un clic de l'utilisateur.
 *
 * Actions (sur l'élément hôte, événements de `toggle`):
 * - toggle:opened->visa-tracker#sync
 * - toggle:closed->visa-tracker#sync
 * - turbo:morph-element->visa-tracker#morphed (remonte des descendants : seul l'hôte compte)
 * - hashchange@window->visa-tracker#hashChanged et click@document->visa-tracker#linkClicked (avec `anchor`)
 *
 * @example
 * <div data-controller="toggle visa-tracker"
 *      data-toggle-open-value="false"
 *      data-action="toggle:opened->visa-tracker#sync toggle:closed->visa-tracker#sync turbo:morph-element->visa-tracker#morphed"
 *      data-visa-tracker-show-label-value="Voir le suivi"
 *      data-visa-tracker-hide-label-value="Masquer le suivi"
 *      data-visa-tracker-storage-key-value="planning-validation-42">
 *   <button data-toggle-target="trigger" data-action="click->toggle#toggle">
 *     <span data-visa-tracker-target="label">Voir le suivi</span>
 *   </button>
 *   <div data-toggle-target="content" class="hidden">…</div>
 * </div>
 */
export default class extends Controller {
  static targets = ['label', 'trigger'];

  static values = {
    showLabel: String,
    hideLabel: String,
    showAriaLabel: String,
    hideAriaLabel: String,
    storageKey: String,
    anchor: String,
  };

  connect() {
    this.ready = false;
    const stored = this.readStoredState();
    if (stored !== null && stored !== this.isOpen()) {
      // `toggle` observe son attribut de valeur : il ouvre/ferme et émet toggle:opened/closed.
      this.element.setAttribute('data-toggle-open-value', String(stored));
    }
    this.renderLabel(stored ?? this.isOpen());
    this.ready = true;
    this.openIfTargeted();
  }

  // QA-PR1-19 : le hash de la page vise l'ancre du suivi (ligne « Validation » d'« À traiter »).
  hashChanged() {
    this.openIfTargeted();
  }

  // Le hash est déjà le bon (second clic sur la ligne) : aucun `hashchange`, le clic du lien ouvre quand même.
  linkClicked(event) {
    if (!this.anchorValue) return;

    const link = event.target?.closest?.('a[href]');
    if (link?.getAttribute('href') === `#${this.anchorValue}`) this.open();
  }

  openIfTargeted() {
    if (this.anchorValue && window.location?.hash === `#${this.anchorValue}`) this.open();
  }

  open() {
    if (!this.isOpen()) this.element.setAttribute('data-toggle-open-value', 'true');
    this.renderLabel(true);
    this.writeStoredState(true);
  }

  sync(event) {
    const isOpen = event.type.endsWith(':opened');
    this.renderLabel(isOpen);
    if (this.ready && !this.restoring) this.writeStoredState(isOpen);
  }

  morphed(event) {
    if (event.target !== this.element) return;

    const stored = this.readStoredState();
    if (stored === null) return; // rien de mémorisé : l'état du serveur fait foi, comme à la connexion

    this.restoring = true;
    queueMicrotask(() => {
      this.restoring = false;
      if (this.isOpen() === stored) {
        this.renderLabel(stored);
      } else {
        this.element.setAttribute('data-toggle-open-value', String(stored));
      }
    });
  }

  isOpen() {
    return this.element.getAttribute('data-toggle-open-value') === 'true';
  }

  renderLabel(isOpen) {
    this.renderAriaLabel(isOpen);
    if (!this.hasLabelTarget) return;

    const label = isOpen ? this.hideLabelValue : this.showLabelValue;
    if (label) this.labelTarget.textContent = label;
  }

  renderAriaLabel(isOpen) {
    if (!this.hasTriggerTarget) return;

    const ariaLabel = isOpen ? this.hideAriaLabelValue : this.showAriaLabelValue;
    if (ariaLabel) this.triggerTarget.setAttribute('aria-label', ariaLabel);
  }

  readStoredState() {
    if (!this.storageKeyValue) return null;

    try {
      const raw = window.sessionStorage.getItem(this.storageKeyValue);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      // stockage inaccessible : l'état rendu par le serveur fait foi
    }
    return null;
  }

  writeStoredState(isOpen) {
    if (!this.storageKeyValue) return;

    try {
      window.sessionStorage.setItem(this.storageKeyValue, String(isOpen));
    } catch {
      // stockage inaccessible : la bascule fonctionne, sans mémoire
    }
  }
}
